import type {
  DateRequestErrors,
  PossibleInvalidKeys,
} from '~/components/date-request/dateRequestTypes'

export interface DateRequestApiErrorData {
  message?: string
  reason?: string
  errors?: Record<string, string[]>
}

export interface MapDateRequestApiErrorsResult {
  fieldErrors: DateRequestErrors
  invalidKeys: PossibleInvalidKeys[]
  submitError: string | null
}

const API_FIELD_TO_FORM_FIELD: Record<string, PossibleInvalidKeys> = {
  email: 'email',
  first_name: 'name',
  last_name: 'surname',
  phone: 'phone',
  number_of_tickets: 'numberOfTickets',
  dates: 'dates',
  date_type: 'dateType',
  date: 'dates',
  more_info: 'additionalInfo',
  customer_message: 'customerMessage',
  company_name: 'companyName',
  city: 'city',
}

const isApiErrorData = (value: unknown): value is DateRequestApiErrorData => {
  if (!value || typeof value !== 'object') {
    return false
  }

  const candidate = value as DateRequestApiErrorData
  return (
    typeof candidate.message === 'string' ||
    typeof candidate.reason === 'string' ||
    (candidate.errors !== undefined && typeof candidate.errors === 'object')
  )
}

/**
 * ofetch / $fetch may expose the JSON body on `data` or `response._data`
 * depending on runtime. Prefer a body that includes validation fields.
 */
export const getDateRequestApiErrorData = (
  error: unknown
): DateRequestApiErrorData | undefined => {
  const e = error as {
    data?: unknown
    response?: { _data?: unknown; data?: unknown }
  }

  const candidates = [e?.data, e?.response?._data, e?.response?.data]

  for (const candidate of candidates) {
    if (isApiErrorData(candidate)) {
      return candidate
    }
  }

  return undefined
}

const resolveFormField = (apiKey: string): PossibleInvalidKeys | null => {
  const direct = API_FIELD_TO_FORM_FIELD[apiKey]
  if (direct) {
    return direct
  }

  if (apiKey === 'dates' || /^dates\.\d+$/.test(apiKey)) {
    return 'dates'
  }

  return null
}

const parseDateSlotIndex = (apiKey: string): number | null => {
  const match = apiKey.match(/^dates\.(\d+)$/)
  if (!match) {
    return null
  }

  return Number(match[1])
}

export const mapDateRequestApiErrors = (
  data?: DateRequestApiErrorData
): MapDateRequestApiErrorsResult => {
  const emptyResult: MapDateRequestApiErrorsResult = {
    fieldErrors: {},
    invalidKeys: [],
    submitError: null,
  }

  if (!data) {
    return emptyResult
  }

  if (data.reason !== 'requestValidation' || !data.errors) {
    return {
      ...emptyResult,
      submitError: data.message ?? null,
    }
  }

  const fieldErrors: DateRequestErrors = {}
  const invalidKeys: PossibleInvalidKeys[] = []
  const unmappedMessages: string[] = []
  const dateSlots: Record<number, string> = {}

  const pushInvalidKey = (key: PossibleInvalidKeys) => {
    if (!invalidKeys.includes(key)) {
      invalidKeys.push(key)
    }
  }

  for (const [apiKey, messages] of Object.entries(data.errors)) {
    const message = messages?.find((entry) => entry.trim().length > 0)
    if (!message) {
      continue
    }

    const formField = resolveFormField(apiKey)
    if (!formField) {
      unmappedMessages.push(message)
      continue
    }

    const slotIndex = parseDateSlotIndex(apiKey)
    if (slotIndex !== null) {
      dateSlots[slotIndex] = message
      pushInvalidKey('dates')
      continue
    }

    if (formField === 'dates') {
      fieldErrors.dates = message
      pushInvalidKey('dates')
      continue
    }

    fieldErrors[formField] = message
    pushInvalidKey(formField)
  }

  if (Object.keys(dateSlots).length > 0) {
    fieldErrors.dateSlots = dateSlots
  }

  const submitError =
    unmappedMessages.length > 0
      ? unmappedMessages.join(' ')
      : invalidKeys.length === 0
        ? (data.message ?? null)
        : null

  return {
    fieldErrors,
    invalidKeys,
    submitError,
  }
}
